import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth';
import { useAuthRedirect } from '../../hooks/useAuthRedirect';
import { useFieldValidation, validateEmail, validatePassword } from '../../utils/validation';
import FormInput from './FormInput';
import './Auth.css';

export default function Login() {
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signInWithGoogle } = useAuth();
  const navigate = useNavigate();

  // Redirect to dashboard if user is already logged in
  useAuthRedirect('/dashboard', true);

  // Field validation hooks
  const emailField = useFieldValidation('', validateEmail);
  const passwordField = useFieldValidation('', validatePassword);

  async function handleSubmit(e) {
    e.preventDefault();

    // Validate all fields
    if (!emailField.isValid || !passwordField.isValid) {
      setFormError('Please fix the errors above');
      return;
    }

    try {
      setFormError('');
      setLoading(true);
      await login(emailField.value, passwordField.value);
      // Navigate immediately after successful login
      navigate('/dashboard');
    } catch (error) {
      setFormError('Failed to sign in: ' + error.message);
    }
    setLoading(false);
  }

  async function handleGoogleSignIn() {
    try {
      setFormError('');
      setLoading(true);
      await signInWithGoogle();
      // Navigate immediately after successful Google sign-in
      navigate('/dashboard');
    } catch (error) {
      setFormError('Failed to sign in with Google: ' + error.message);
    }
    setLoading(false);
  }

  return (
    <div className="auth-card">
      <h2>Sign In</h2>
      {formError && <div className="auth-error">{formError}</div>}
      
      <form onSubmit={handleSubmit}>
        <FormInput
          label="Email"
          type="email"
          value={emailField.value}
          onChange={emailField.setValue}
          onBlur={emailField.handleBlur}
          error={emailField.error}
          touched={emailField.touched}
          required
          autoComplete="email"
          placeholder="Enter your email"
        />
        
        <FormInput
          label="Password"
          type="password"
          value={passwordField.value}
          onChange={passwordField.setValue}
          onBlur={passwordField.handleBlur}
          error={passwordField.error}
          touched={passwordField.touched}
          required
          autoComplete="current-password"
          placeholder="Enter your password"
        />
        
        <button disabled={loading} type="submit" className="auth-button">
          {loading ? 'Signing In...' : 'Sign In'}
        </button>
      </form>
      
      <div className="auth-divider">
        <span>or</span>
      </div>
      
      <button 
        onClick={handleGoogleSignIn} 
        disabled={loading}
        className="google-button"
      >
        <svg className="google-icon" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Sign in with Google
      </button>
      
      <div className="auth-links">
        <a href="/forgot-password">Forgot Password?</a>
        <a href="/signup">Need an account? Sign Up</a>
      </div>
    </div>
  );
} 